import React, {useEffect, useState} from 'react';
import axios from "axios";
import {MeasurementDevicesApi} from "api/MeasurementDevicesApi";
import {CircleMarker, Marker, useMap, ZoomControl} from "react-leaflet";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import L, {divIcon, Icon, LatLngTuple} from "leaflet";
import {MeasurementDeviceGeoDTO} from "api/model/measurement-devices/MeasurementDeviceGeoDTO";
import {useGetRecentData} from "api/api-hooks/datasets-hooks";
import {Dialog} from "primereact/dialog";

const measurementOptions = [
    {label: "None", value: "NONE"},
    {label: "Supply temperature", value: "SUPPLY_TEMPERATURE"},
    {label: "Return Temperature", value: "RETURN_TEMPERATURE"},
    {label: "Supply pressure", value: "SUPPLY_PRESSURE"},
    {label: "Return Pressure", value: "RETURN_PRESSURE"},
    {label: "Flow rate", value: "FLOW_RATE"},
    {label: "Power", value: "POWER"},
    {label: "Hot water temperature", value: "HOT_WATER_TEMPERATURE"},
];

const MapContent = ({mapRef}) => {
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isInputVisible, setIsInputVisible] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [selectedMeasurementType, setSelectedMeasurementType] = useState<string>("NONE");
    const [selectedAddress, setSelectedAddress] = useState<any | null>(null);
    const [devicesSelectedMeasurement, setDevicesSelectedMeasurement] = useState({});
    const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
    const [displayDialog, setDisplayDialog] = useState<boolean>(false);
    const [dialogTitle, setDialogTitle] = useState<string>("");
    const [dialogData, setDialogData] = useState([])

    const [devicesGeoCoordinates, setDevicesGeoCoordinates] = useState<MeasurementDeviceGeoDTO[] | undefined>();
    const [devicesIds, setDevicesIds] = useState<string[] | undefined>();
    const [firstCoordinatesArray, setFirstCoordinatesArray] = useState<LatLngTuple>([0, 0]);

    const [devicesByTags, setDevicesByTags] = useState<any>();
    const [extractedDatasets, setExtractedDatasets] = useState([])
    const [requiredDatasets, setRequiredDatasets] = useState([])

    // Webpack breaks Leaflet default icon, need this to fix it.
    L.Icon.Default.mergeOptions({
        iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png").default,
        iconUrl: require("leaflet/dist/images/marker-icon.png").default,
        shadowUrl: require("leaflet/dist/images/marker-shadow.png").default,
    });

    const defaultIcon = new Icon.Default();

    useEffect(() => {
        if (selectedMeasurementType !== "NONE" && devicesIds) {
            MeasurementDevicesApi.getMeasurementDevicesReadings(selectedMeasurementType, devicesIds.join(",")).then(
                (response) => {
                    setDevicesSelectedMeasurement(response.data);
                }
            ).catch((error) => {
                console.log(error);
            });
        }
    }, [selectedMeasurementType]);

    const onMeasurementTypeChange = (e: { value: string }) => {
        setSelectedMeasurementType(e.value);
    };

    const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
        setSearchQuery(e.target.value);
    };

    useGetRecentData(requiredDatasets, {
        options:
            {
                enabled: displayDialog,
                refetchInterval: 10000,
                onSuccess: (data) => {
                    setDialogData(data.data)
                },
                onError: () => {
                    console.log("error fetching recent data")
                }

            }
    })

    const hideDialog = () => {
        setDisplayDialog(false);
    };

    useEffect(() => {
        MeasurementDevicesApi.getGeoMeasurementsDevices()
            .then((response) => {
                setDevicesGeoCoordinates(response.data);
            })
            .catch((error) => {
                console.log(error);
            });
        MeasurementDevicesApi.getMeasurementDevicesByTags().then((response) => {
            setDevicesByTags(response.data);
        }).catch((error) => {
            console.log(error);
        });
    }, []);

    useEffect(() => {
        if (devicesGeoCoordinates) {
            const deviceIds = devicesGeoCoordinates.map((device) => device.properties?.dbId);
            setDevicesIds(deviceIds);
        }

        if (devicesGeoCoordinates && devicesGeoCoordinates.length > 0) {
            const firstDeviceCoordinates = devicesGeoCoordinates[0].geometry?.coordinates as LatLngTuple;
            setFirstCoordinatesArray(firstDeviceCoordinates);
        }
    }, [devicesGeoCoordinates]);

    useEffect(() => {
        if (devicesByTags) {
            extractDatasets()
        }
    }, [devicesByTags]);

    const extractDatasets = () => {
        const idAndDatasetArray = devicesByTags.map(device => {
            const datasets = []
            device.deviceDatasets.map(dataset => {
                datasets.push(`${dataset.categorizedDatasetId}:CATEGORIZED_DATASET`)
            })
            return {id: device.id, datasets}
        })
        setExtractedDatasets(idAndDatasetArray)
    }

    const dialogHandler = (deviceId: string, title: string) => {
        const datasets = extractedDatasets.find(dataset => dataset.id === deviceId).datasets
        setRequiredDatasets(datasets)
        setDisplayDialog(true)
        setDialogTitle(title)
    };

    const handleSelectAddress = (result: any) => {
        setSelectedAddress(result);

        const latitude = parseFloat(result.lat);
        const longitude = parseFloat(result.lon);

        if (!isNaN(latitude) && !isNaN(longitude) && mapRef.current) {
            mapRef.current.setView([latitude, longitude], 15);
        }
        setSearchResults([]);
        setSearchValue(result.display_name);
    };

    useEffect(() => {
        const apiUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            searchQuery
        )}&countrycodes=lt&limit=5`;

        axios.get(apiUrl).then((response) => {
            setSearchResults(response.data);
        });
    }, [searchQuery]);

    const map = useMap();

    const markerBounds = new L.LatLngBounds([...firstCoordinatesArray], [...firstCoordinatesArray]);
    devicesGeoCoordinates?.forEach((marker) => {
        const coordinates = marker.geometry.coordinates;
        const leafletCoordinateArray = new L.LatLng(coordinates[0], coordinates[1]);

        markerBounds.extend(leafletCoordinateArray);
    });

    map.fitBounds(markerBounds);

    return (
        <div>
            {selectedAddress && (
                <CircleMarker
                    center={[selectedAddress.lat, selectedAddress.lon]}
                    radius={12}
                    fillColor="blue"
                    fillOpacity={0.2}
                />
            )}
            {devicesGeoCoordinates &&
                devicesGeoCoordinates.length > 0 &&
                devicesGeoCoordinates?.map((device) => {
                    const position = device.geometry?.coordinates as LatLngTuple;

                    const measurementValueObj = devicesSelectedMeasurement[device.properties?.dbId];
                    const measuredValueText = measurementValueObj
                        ? `${measurementValueObj.value} ${measurementValueObj.unit}`
                        : "N\\A";

                    const icon = divIcon({
                        className: "custom-icon",
                        html: `<div>${measuredValueText}</div>`,
                    });

                    return (
                        <Marker
                            key={device.properties?.dbId}
                            position={position}
                            icon={selectedMeasurementType === "NONE" ? defaultIcon : icon}
                            eventHandlers={{
                                click: () => {
                                    setSelectedTitle(device.properties?.title);
                                    setSelectedAddress(null);
                                },
                                dblclick: () => {
                                    dialogHandler(device.properties?.dbId, device.properties?.title);
                                },
                            }}
                        />
                    );
                })}
            <ZoomControl position="bottomright"/>

            <span className="p-input-icon-left " style={{zIndex: 1000, marginLeft: "10px", marginTop: "10px"}}>
                    <InputText
                        value={searchValue}
                        onChange={handleSearchQueryChange}
                        placeholder="Search"
                        onFocus={() => setIsInputVisible((prevState) => !prevState)}
                        style={{
                            width: `${isInputVisible ? "inherit" : "0px"}`,
                        }}
                    />
                    <ul className="absolute top-[100px] flex flex-column"
                        style={{
                            gap: "5px"
                        }}
                    >
                        {searchResults.map((result, index) => (
                            <li
                                key={index}
                                style={{cursor: "pointer", padding: "5px", backgroundColor: "white"}}
                                onClick={() => handleSelectAddress(result)}
                            >
                                {result.display_name}
                            </li>
                        ))}
                    </ul>
                </span>
            <div
                style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    zIndex: 1000,
                }}
            >
                <Dropdown
                    value={selectedMeasurementType}
                    options={measurementOptions}
                    onChange={onMeasurementTypeChange}
                />
            </div>
            {selectedTitle && !displayDialog && (
                <div id="popup">
                    <h3>{selectedTitle}</h3>
                </div>
            )}
            <Dialog
                header={`Device latest measurements`}
                visible={displayDialog}
                onHide={hideDialog}
                style={{width: "600px", height: "600px"}}
            >
                <h2>{dialogTitle}</h2>
                <h4 className="border-top-1 border-bottom-1 border-gray-100 pt-3 pb-3">{(dialogData[0]?.data[0]?.key)?.replace("T", " ").replace("Z", "")}</h4>
                <div className="flex flex-column gap-2">
                    {dialogData?.map(device => {
                        return (<div className="flex justify-content-between">
                            <p>{device.title}</p>
                            <p>{`${device.data[0].value} ${device.unit}`}</p>
                        </div>)
                    })}
                </div>

            </Dialog>
        </div>
    )
}

export default MapContent;